import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CurrencySwitcher from './CurrencySwitcher';
import {
  INSTAGRAM_HANDLE,
  INSTAGRAM_URL,
  PHONE_DISPLAY,
  WHATSAPP_BASE,
  withBase,
} from '@/constants';

const NAV_ITEMS = [
  { key: 'nav.garage', href: withBase('/#garaj') },
  { key: 'nav.villa', href: withBase('/#villa') },
  { key: 'nav.winter', href: withBase('/#kis-bahcesi') },
  { key: 'nav.projects', href: withBase('/#projeler') },
  { key: 'nav.about', href: withBase('/#hakkimizda') },
  { key: 'nav.dealer', to: '/bayilik' },
  { key: 'nav.contact', href: withBase('/#iletisim') },
];

const LANGUAGES = [
  { code: 'tr', label: 'TR' },
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
  { code: 'fr', label: 'FR' },
  { code: 'ar', label: 'AR' },
  { code: 'ru', label: 'RU' },
];

export default function Footer() {
  const { t, i18n } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-foreground-950 text-background-100">
      <div className="border-t border-primary-500/20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-14 md:px-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-4">
            <Link to="/" aria-label="VILLAGARAGE">
              <img
                src="https://storage.helloreaddy.io/project_files/23cbf37b-3b60-45d6-b458-2fbb3e9ec10a/38c06273-6207-48ae-a08c-ac1e41dc7367_compressed_logo.webp"
                alt="VILLAGARAGE Premium Garage Systems"
                className="h-16 w-auto object-contain"
              />
            </Link>
            <Link
              to="/bayilik"
              className="flex items-center gap-2 whitespace-nowrap text-sm text-background-100/80 transition-colors hover:text-primary-400"
            >
              <i className="ri-store-2-line text-base" aria-hidden="true"></i>
              {t('nav.dealer')}
            </Link>
            <Link
              to="/giris"
              className="flex items-center gap-2 whitespace-nowrap text-sm text-background-100/80 transition-colors hover:text-primary-400"
            >
              <i className="ri-user-line text-base" aria-hidden="true"></i>
              {t('nav.login')}
            </Link>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-primary-500">
              {t('footer.navTitle')}
            </h4>
            <nav className="mt-5 flex flex-col gap-3">
              {NAV_ITEMS.map((item) =>
                item.to ? (
                  <Link
                    key={item.key}
                    to={item.to}
                    className="whitespace-nowrap text-sm text-background-100/80 transition-colors hover:text-primary-400"
                  >
                    {t(item.key)}
                  </Link>
                ) : (
                  <a
                    key={item.key}
                    href={item.href}
                    className="whitespace-nowrap text-sm text-background-100/80 transition-colors hover:text-primary-400"
                  >
                    {t(item.key)}
                  </a>
                ),
              )}
            </nav>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-primary-500">
              {t('footer.contactTitle')}
            </h4>
            <div className="mt-5 flex flex-col gap-3">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-2 text-sm text-background-100/80 transition-colors hover:text-primary-400"
              >
                <i className="ri-instagram-line text-base" aria-hidden="true"></i>
                <span>{INSTAGRAM_HANDLE}</span>
              </a>
              <a
                href={`${WHATSAPP_BASE}?text=${encodeURIComponent(t('whatsapp.message'))}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-2 text-sm text-background-100/80 transition-colors hover:text-primary-400"
              >
                <i className="ri-whatsapp-line text-base" aria-hidden="true"></i>
                <span dir="ltr">{PHONE_DISPLAY}</span>
              </a>
              <a
                href={`tel:${PHONE_DISPLAY.replace(/\s/g, '')}`}
                className="flex items-center gap-2 text-sm text-background-100/80 transition-colors hover:text-primary-400"
              >
                <i className="ri-phone-line text-base" aria-hidden="true"></i>
                <span dir="ltr">{PHONE_DISPLAY}</span>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-primary-500">
              {t('currency.label')}
            </h4>
            <div className="mt-5">
              <CurrencySwitcher dark />
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                    i18n.language === lang.code
                      ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                      : 'border-foreground-700 text-background-100/70 hover:border-primary-500/60 hover:text-primary-400'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-foreground-800">
          <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-6 text-center sm:flex-row md:px-6">
            <p className="text-xs text-background-100/50">
              © {year} VILLAGARAGE. {t('footer.copyright')}
            </p>
            <p className="text-xs uppercase tracking-[0.25em] text-primary-400/70">
              {t('brand.tagline')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}